import React, { FC, useContext, useEffect, useRef, useState } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import moment from 'moment';
import { Hash } from 'viem';

import { useVestaSubgraph } from '~components/hooks/graph/useVestaSubgraph';
import { useSession } from '~components/hooks/useSession';
import { lookupDisplayError } from '~utils/error/errorHandlers';
import { VestaError } from '~utils/error/errors';
import { getTransactionBlock, getTxUrl } from '~utils/utils';

import { OnTransactionSuccess, TxContext, TxHistorical, TxInstance, TxStatus } from './types';

const IS_TRANSACTING_STATES = ['confirming', 'sending'];
const NON_TRANSACTING_STATES = [TxStatus.idle, TxStatus.error, TxStatus.success];
const COMPLETE_STATES = [TxStatus.error, TxStatus.success];
export const HIDE_TX_NOTIFICATION_TIMEOUT_MS = 10_000;

export const TransactionContext = React.createContext<TxContext | undefined>(undefined);

export const useTxHistory = () => {
  const HISTORY_SESSION_KEY = '_TX_HISTORY';
  const [history, setHistory] = useState<TxHistorical[]>([]);

  useEffect(() => {
    if (window) {
      const historySession = window.sessionStorage.getItem(HISTORY_SESSION_KEY);

      if (historySession) {
        const sessionTxs = JSON.parse(historySession) as TxHistorical[];
        setHistory(sessionTxs);
      } else {
        window.sessionStorage.setItem(HISTORY_SESSION_KEY, '[]');
      }
    }
  }, []);

  function addToHistory(data: TxHistorical) {
    setHistory(last => {
      return [...last, data];
    });
    window.sessionStorage.setItem(HISTORY_SESSION_KEY, JSON.stringify(history));
  }

  function clearHistory() {
    setHistory([]);
    if (window) window.sessionStorage.setItem(HISTORY_SESSION_KEY, '[]');
  }

  return {
    history,
    addToHistory,
    clearHistory,
  };
};

export const TransactionProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { publicClient, address } = useSession();
  const [transactions, setTransactions] = useState<Record<string, TxInstance>>({});
  // Access this variable for the most accurate value of transactions since if you setState multiple times in one function it is not updated syncrhonously
  const inMemoryTransactionsRef = useRef<Record<string, TxInstance>>({});
  const { history, clearHistory, addToHistory } = useTxHistory();

  const subgraph = useVestaSubgraph();

  const [showTransaction, setShowTransaction] = useState(false);
  const showTransactionTimeoutRef = useRef<NodeJS.Timeout>();

  const [currentTransactionKey, setCurrentTransactionKey] = useState('');
  const [prevTransactionKey, setPrevTransactionKey] = useState('');

  const updateTransaction = ({
    key,
    tx,
  }: {
    key: string;
    tx: { status: TxStatus } & Partial<TxInstance>;
  }) => {
    const updatedTransaction = {
      ...transactions[key],
      isTransacting: IS_TRANSACTING_STATES.includes(tx.status),
      complete: COMPLETE_STATES.includes(tx.status),
      ...tx,
      key,
    } as TxInstance;

    const updatedTransactions = {
      ...inMemoryTransactionsRef.current,
      [key]: updatedTransaction,
    };

    inMemoryTransactionsRef.current = updatedTransactions;
    setTransactions(updatedTransactions);

    return updatedTransaction;
  };

  function addFinalTxToHistory(tx: TxInstance) {
    const txFinishTimestamp = moment.utc(new Date().toUTCString()).utcOffset(-new Date().getTimezoneOffset());
    addToHistory({
      ...tx,
      timestampUnix: Date.now(),
      timestampExplicit: `${txFinishTimestamp.format('MMM D, h:mm:ss A')}`,
    });
  }

  const resetTx = (key: string) => {
    addFinalTxToHistory(inMemoryTransactionsRef.current[key]);
    const timeoutId = setTimeout(() => {
      setShowTransaction(false);
      updateTransaction({ key, tx: { status: TxStatus.idle } });
    }, HIDE_TX_NOTIFICATION_TIMEOUT_MS);
    showTransactionTimeoutRef.current = timeoutId;
  };

  const onTransactionSucessWrapper = async ({
    key,
    successBlock,
    onTransactionSuccess,
  }: {
    key: string;
    successBlock: number;
    onTransactionSuccess: OnTransactionSuccess;
  }) => {
    const doCallback = async () => {
      await onTransactionSuccess.callback();
    };

    try {
      if (onTransactionSuccess.checkBlock) {
        //if this call fails, it means the subgraph is behind.
        await subgraph.checkBlock(successBlock);
        doCallback();
      } else {
        doCallback();
      }
    } catch {
      setTimeout(onTransactionSucessWrapper, 1500, { key, successBlock, onTransactionSuccess });
    }
  };

  const getTx = (key: string): TxInstance | undefined => inMemoryTransactionsRef.current[key];

  const createTxInstance = ({
    key,
    nextStepName = '',
    override = false,
  }: {
    key: string;
    nextStepName?: string;
    override?: boolean;
  }) => {
    if (override) {
      setShowTransaction(false);
      clearTimeout(showTransactionTimeoutRef.current);
      showTransactionTimeoutRef.current = undefined;
    }

    if (!override && key in inMemoryTransactionsRef.current) return;

    const baseTx = {
      key,
      nextStepName,
      status: TxStatus.idle,
      complete: false,
      isTransacting: false,
      url: '',
      error: '',
    };

    updateTransaction({ key, tx: baseTx });
  };

  const transact = async ({
    key,
    writeContract,
    onTransactionSuccess,
  }: {
    key: string;
    writeContract: () => Promise<Hash>;
    onTransactionSuccess: OnTransactionSuccess;
  }) => {
    const inMemoryTransactions = inMemoryTransactionsRef.current;
    const currentStatus = inMemoryTransactions[key]?.status;
    if (key in inMemoryTransactions && !NON_TRANSACTING_STATES.includes(currentStatus)) {
      return undefined;
    }

    if (showTransactionTimeoutRef.current) {
      // clear last called transaction immediately
      updateTransaction({ key: currentTransactionKey, tx: { status: TxStatus.idle } });
      clearTimeout(showTransactionTimeoutRef.current);
      showTransactionTimeoutRef.current = undefined;
    }

    if (inMemoryTransactions[currentTransactionKey]?.nextStepName === key) {
      setPrevTransactionKey(currentTransactionKey);
    } else {
      setPrevTransactionKey('');
    }

    setCurrentTransactionKey(key);

    updateTransaction({
      key,
      tx: {
        status: TxStatus.confirming,
      },
    });

    setShowTransaction(true);

    try {
      const confirmRes = await writeContract();

      const url = getTxUrl(confirmRes);

      updateTransaction({
        key,
        tx: {
          url,
          status: TxStatus.sending,
        },
      });

      const successBlock = await getTransactionBlock(confirmRes, publicClient);

      if (!successBlock) {
        throw 'Transaction Failed!';
      }

      amplitude.track('transaction', { type: key, status: TxStatus.success });

      updateTransaction({
        key,
        tx: {
          status: TxStatus.success,
          url,
        },
      });

      resetTx(key);

      onTransactionSucessWrapper({ key, successBlock, onTransactionSuccess });
    } catch (err) {
      amplitude.track('transaction', { type: key, status: TxStatus.error });

      const error = lookupDisplayError('error' in (err as any) ? (err as any).error : err);

      updateTransaction({
        key,
        tx: {
          status: TxStatus.error,
          error,
        },
      });

      resetTx(key);
    }
  };

  useEffect(() => {
    clearHistory();
    setTransactions({});
    setCurrentTransactionKey('');
    setPrevTransactionKey('');
  }, [address]);

  return (
    <TransactionContext.Provider
      value={{
        transactions,
        history,
        transact,
        createTxInstance,
        getTx,
        clearHistory,
        currentTransactionKey,
        prevTransactionKey,
        showTransaction,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransactionProvider = () => {
  const context = useContext(TransactionContext);

  useEffect(() => {
    if (!context) throw new VestaError('No tx provider detected;');
  });

  return context as TxContext;
};
